import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import usePreviousRef from 'hooks/usePreviousRef';
import {
  hideModal,
  isActiveSelector,
  modalParamsSelector,
  popModal,
  popModalIfActive,
} from 'redux/modules/modal';
import { ModalName } from 'redux/modules/modal/types';
import { Dispatch } from 'redux/types';

interface Modal {
  name: string;
  params?: any;
}

export type UseConnectedModalOptions = {
  closeModalOnUnmount?: boolean;
};

export interface UseConnectedModalReturnType<T extends Modal = any> {
  onExited: () => void;
  onHide: (value?: any) => any;
  params: T['params'];
  show: boolean;
}

export default function useConnectedModal<T extends Modal = any>(
  name: ModalName,
  options?: UseConnectedModalOptions,
): UseConnectedModalReturnType<T> {
  const { closeModalOnUnmount = true } = options || {};

  const dispatch = useDispatch<Dispatch>();
  const onHide = useCallback(value => dispatch(hideModal(value)), [dispatch]);
  const onExited = useCallback(() => dispatch(popModal()), [dispatch]);
  const show = useSelector(isActiveSelector(name));
  const params = useSelector(modalParamsSelector);

  const closeModalOnUnmountRef = usePreviousRef(closeModalOnUnmount);

  // cleanup modal if it is still active after unmount
  useEffect(
    () => () => {
      if (closeModalOnUnmountRef.current) {
        dispatch(popModalIfActive(name));
      }
    },
    [dispatch, name, closeModalOnUnmountRef],
  );

  return {
    onExited,
    onHide,
    params,
    show,
  };
}
